import type { AdminClient } from '@apx/admin-sdk';
import { escapeHtml } from './reports-shared.js';

export interface RetentionDeps {
  client: AdminClient;
  integratorId: string;
}

/**
 * Retention tab — daily active (new vs returning) + D1/D7 cohort rates for one
 * game. Game is chosen from a dropdown of the integrator's registered games
 * (retention endpoints are per-game, so "all" is not offered).
 */
export async function renderRetentionTab(
  root: HTMLElement,
  deps: RetentionDeps,
  meta: { title: string; sub: string },
  games: string[],
): Promise<void> {
  const initialGame = games[0] ?? '';
  root.innerHTML = `
    <h1 class="page-title">${escapeHtml(meta.title)}</h1>
    <p class="page-sub">${escapeHtml(meta.sub)}</p>
    <div class="card report-filters">
      <div class="filter-row">
        <label class="filter-field">Game
          <select id="ret-game" class="field-input">
            ${games.length === 0 ? '<option value="">(no games)</option>' : ''}
            ${games.map((g) => `<option value="${escapeHtml(g)}"${g === initialGame ? ' selected' : ''}>${escapeHtml(g)}</option>`).join('')}
          </select>
        </label>
        <label class="filter-field">Window
          <select id="ret-days" class="field-input">
            <option value="7">Last 7 days</option>
            <option value="30" selected>Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </label>
        <button id="ret-run" class="primary">Refresh</button>
      </div>
    </div>
    <div id="ret-out"></div>
  `;

  const out = root.querySelector<HTMLElement>('#ret-out')!;
  const gameSelect = root.querySelector<HTMLSelectElement>('#ret-game')!;
  const daysSelect = root.querySelector<HTMLSelectElement>('#ret-days')!;
  const runBtn = root.querySelector<HTMLButtonElement>('#ret-run')!;

  const pct = (r: number | null): string => (r === null ? '—' : `${(r * 100).toFixed(1)}%`);

  async function load(): Promise<void> {
    const gameId = gameSelect.value.trim();
    if (gameId === '') {
      out.innerHTML = `<div class="card"><p class="muted">No game selected — register a game first.</p></div>`;
      return;
    }
    out.innerHTML = `<div class="card"><p class="muted">Loading…</p></div>`;
    runBtn.disabled = true;
    try {
      const r = await deps.client.fetchRetention(deps.integratorId, gameId, {
        days: Number(daysSelect.value),
      });
      const rows = r.daily
        .slice()
        .reverse()
        .map(
          (d) =>
            `<tr class="ret-day-row" data-ret-date="${escapeHtml(d.date)}" title="Show players active on ${escapeHtml(d.date)}"><td><span class="row-link">${escapeHtml(d.date)}</span></td><td class="num">${d.active}</td><td class="num pos">${d.new}</td><td class="num">${d.returning}</td></tr>`,
        )
        .join('');
      out.innerHTML = `
        <div class="players-stats">
          <div class="stat-card"><div class="stat-card-label">D1 retention</div><div class="stat-card-value">${pct(r.cohort.d1Rate)}</div><div class="stat-card-sub">cohort ${r.cohort.d1Cohort}</div></div>
          <div class="stat-card"><div class="stat-card-label">D7 retention</div><div class="stat-card-value">${pct(r.cohort.d7Rate)}</div><div class="stat-card-sub">cohort ${r.cohort.d7Cohort}</div></div>
        </div>
        <div class="card">
          <h2>Daily active players (${r.windowDays}d)</h2>
          ${
            rows === ''
              ? `<p class="muted">No bets in this window.</p>`
              : `<div class="data-table-wrap"><table class="data-table"><thead><tr><th>Date</th><th class="num">Active</th><th class="num">New</th><th class="num">Returning</th></tr></thead><tbody>${rows}</tbody></table></div>`
          }
        </div>
        <div id="ret-day-detail"></div>
      `;
      out.querySelectorAll<HTMLElement>('.ret-day-row').forEach((row) => {
        row.addEventListener('click', () => void loadDay(row.dataset.retDate ?? ''));
      });
    } catch (err) {
      out.innerHTML = `<div class="card"><p class="error">Failed to load retention: ${escapeHtml(
        err instanceof Error ? err.message : String(err),
      )}</p></div>`;
    } finally {
      runBtn.disabled = false;
    }
  }

  async function loadDay(date: string): Promise<void> {
    const detail = root.querySelector<HTMLElement>('#ret-day-detail');
    if (!detail || date === '') return;
    const gameId = gameSelect.value.trim();
    detail.innerHTML = `<div class="card"><p class="muted">Loading players for ${escapeHtml(date)}…</p></div>`;
    detail.scrollIntoView({ block: 'nearest' });
    try {
      const r = await deps.client.fetchRetentionDay(deps.integratorId, gameId, {
        date,
        days: Number(daysSelect.value),
      });
      const body =
        r.players.length === 0
          ? `<p class="muted">No active players on this day.</p>`
          : `<div class="data-table-wrap"><table class="data-table">
              <thead><tr><th>Player</th><th>Type</th><th class="num">Bets</th></tr></thead>
              <tbody>${r.players
                .map(
                  (pl) => `<tr class="row-nav" data-row-href="/integrators/${encodeURIComponent(deps.integratorId)}/players/${encodeURIComponent(pl.playerId)}">
                    <td>${escapeHtml(pl.displayName)}</td>
                    <td><span class="pill ${pl.kind === 'new' ? 'live' : 'warn'}">${pl.kind}</span></td>
                    <td class="num">${pl.betCount.toLocaleString()}</td>
                  </tr>`,
                )
                .join('')}</tbody>
            </table></div>`;
      detail.innerHTML = `<div class="card report-section">
        <h2 class="report-section-title">Players active on ${escapeHtml(date)} <span class="muted" style="font-weight:400">· ${r.players.length}</span></h2>
        ${body}
      </div>`;
    } catch (err) {
      detail.innerHTML = `<div class="card"><p class="error">Failed to load players: ${escapeHtml(
        err instanceof Error ? err.message : String(err),
      )}</p></div>`;
    }
  }

  runBtn.addEventListener('click', () => void load());
  gameSelect.addEventListener('change', () => void load());
  await load();
}
